/**
 * Telemetry derivation for the rocket simulator.
 *
 * All telemetry values are derived from the actual simulation state and the
 * applied control action. No values are invented or randomized.
 *
 * Commanded throttle is what the controller requested; effective throttle is
 * what was actually applied after fuel-exhaustion clamping.
 */

import type {
  RocketState,
  ControlAction,
  TelemetryPoint,
  LandingResult,
} from "./types";

/**
 * Derive a telemetry point from a simulation state and applied action.
 *
 * @param state - Current simulation state.
 * @param action - Control action that was applied to reach this state.
 * @param netFX - Net horizontal force applied (N).
 * @param netFZ - Net vertical force applied (N).
 * @param mass - Current total mass (kg).
 */
export function deriveTelemetry(
  state: RocketState,
  action: ControlAction,
  netFX: number,
  netFZ: number,
  mass: number
): TelemetryPoint {
  const speed = Math.sqrt(state.vx * state.vx + state.vz * state.vz);
  const ax = netFX / mass;
  const az = netFZ / mass;
  const accelMagnitude = Math.sqrt(ax * ax + az * az);

  return {
    t: state.t,
    x: state.x,
    z: state.z,
    vx: state.vx,
    vz: state.vz,
    speed,
    altitude: state.z,
    fuel: state.fuel,
    mass,
    throttle: action.throttle,
    effectiveThrottle: action.throttle,
    gimbal: action.gimbal,
    theta: state.theta,
    omega: state.omega,
    ax,
    az,
    accelMagnitude,
  };
}

/**
 * Derive telemetry at impact, including landing result fields.
 */
export function deriveImpactTelemetry(
  state: RocketState,
  action: ControlAction,
  netFX: number,
  netFZ: number,
  mass: number,
  impactTime: number,
  landingTargetX: number,
  maxTouchdownVerticalSpeed: number,
  maxTouchdownHorizontalSpeed: number,
  maxHorizontalError: number,
  maxTouchdownAttitude: number,
  maxTouchdownAngularRate: number
): { telemetry: TelemetryPoint; landing: LandingResult } {
  const telemetry = deriveTelemetry(state, action, netFX, netFZ, mass);

  const touchdownVx = state.vx;
  const touchdownVz = state.vz;
  const touchdownSpeed = Math.sqrt(touchdownVx * touchdownVx + touchdownVz * touchdownVz);
  const horizontalError = Math.abs(state.x - landingTargetX);

  // Normalize pitch angle to [-pi, pi] for comparison.
  let thetaNorm = ((state.theta + Math.PI) % (2 * Math.PI) + (2 * Math.PI)) % (2 * Math.PI) - Math.PI;
  if (thetaNorm > Math.PI) {
    thetaNorm -= 2 * Math.PI;
  }
  if (thetaNorm < -Math.PI) {
    thetaNorm += 2 * Math.PI;
  }

  const landingSuccess =
    Math.abs(touchdownVz) <= maxTouchdownVerticalSpeed &&
    Math.abs(touchdownVx) <= maxTouchdownHorizontalSpeed &&
    horizontalError <= maxHorizontalError &&
    Math.abs(thetaNorm) <= maxTouchdownAttitude &&
    Math.abs(state.omega) <= maxTouchdownAngularRate;

  const landing: LandingResult = {
    landingSuccess,
    touchdownVx,
    touchdownVz,
    touchdownSpeed,
    horizontalError,
    impactTime,
  };

  return { telemetry, landing };
}

/**
 * Derive telemetry for a non-impact state where z may be clamped at zero but
 * simulation continues (should not happen in normal flow; impact terminates).
 */
export function deriveStateTelemetry(
  state: RocketState,
  action: ControlAction,
  netFX: number,
  netFZ: number,
  mass: number
): TelemetryPoint {
  return deriveTelemetry(state, action, netFX, netFZ, mass);
}
